// Newsletter block model + email HTML renderer — the single source of truth
// shared between the backend sender (backend/src/services/newsletterService.js)
// and the frontend WYSIWYG editor (src/components/UserMenu/NewsletterEditor/).
//
// Keep this in plain ESM JS so Node imports it directly. The companion
// newsletterBlocks.d.ts gives the TypeScript frontend its types.
//
// Rendering is intentionally email-safe: table-based layout, every style
// inlined, no <style> blocks, no flexbox/grid. The same per-block style
// objects feed both the React editor canvas and the serialized HTML so the
// WYSIWYG preview can't drift from what recipients receive.
//
// isomorphic-dompurify works in BOTH the browser bundle and Node (the backend
// email sender imports this module), so rich-text sanitization runs identically
// everywhere prepareRichTextHtml is used.
import DOMPurify from 'isomorphic-dompurify';

// ── Constants ──────────────────────────────────────────────────────────────

// Google-hosted web fonts (Quicksand, Comfortaa) need the stylesheet linked in
// both the app (index.html) and the email <head> (see GOOGLE_FONTS_LINK below).
// Mail clients that don't support web fonts gracefully fall back to the
// sans-serif tail of the stack.
export const FONT_PACKS = Object.freeze([
  // Fallbacks are geometric/rounded (Century Gothic on Windows, Futura on Mac,
  // then Trebuchet/Verdana everywhere) so clients that strip the web font —
  // Gmail, Outlook — still render something close to the rounded look, not the
  // default grotesque system sans.
  { id: 'quicksand', label: 'Quicksand', stack: "'Quicksand','Century Gothic',Futura,'Trebuchet MS',Verdana,sans-serif" },
  { id: 'comfortaa', label: 'Comfortaa', stack: "'Comfortaa','Century Gothic',Futura,'Trebuchet MS',Verdana,sans-serif" },
  // Web-safe geometric/humanist faces — also the Quicksand/Comfortaa fallbacks,
  // exposed here so they can be picked directly (and render identically in every
  // client, no web-font support required).
  { id: 'century-gothic', label: 'Century Gothic', stack: "'Century Gothic',Futura,'Trebuchet MS',Verdana,sans-serif" },
  { id: 'futura', label: 'Futura', stack: "Futura,'Century Gothic','Trebuchet MS',Verdana,sans-serif" },
  { id: 'trebuchet', label: 'Trebuchet MS', stack: "'Trebuchet MS',Verdana,Geneva,sans-serif" },
  { id: 'system', label: 'System Sans', stack: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif" },
  { id: 'georgia', label: 'Georgia Serif', stack: "Georgia,'Times New Roman',Times,serif" },
  { id: 'helvetica', label: 'Helvetica', stack: 'Helvetica,Arial,sans-serif' },
  { id: 'verdana', label: 'Verdana', stack: 'Verdana,Geneva,Tahoma,sans-serif' },
  { id: 'courier', label: 'Courier Mono', stack: "'Courier New',Courier,monospace" },
]);

// Stylesheet that loads the web fonts above. Reused by the app and the email.
export const GOOGLE_FONTS_LINK =
  'https://fonts.googleapis.com/css2?family=Comfortaa:wght@400;500;700&family=Quicksand:wght@400;500;700&display=swap';

export const BUTTON_STYLES = Object.freeze([
  { id: 'rounded', label: 'Rounded', borderRadius: 8 },
  { id: 'pill', label: 'Pill', borderRadius: 999 },
  { id: 'square', label: 'Square', borderRadius: 0 },
]);

// Tokens substituted per-recipient at send time by newsletterService.
export const PERSONALIZATION_TOKENS = Object.freeze([
  { token: '{{display_name}}', label: 'Display name' },
  { token: '{{email}}', label: 'Email address' },
  { token: '{{unsubscribe_url}}', label: 'Unsubscribe link' },
]);

// Block types the editor lets you add. Header/Footer are auto-included and
// not in this list — they always exist on the doc, one each, pinned.
export const ADDABLE_BLOCK_TYPES = Object.freeze(['text', 'image', 'button', 'line', 'spacer']);

export const DEFAULT_THEME = Object.freeze({
  emailBgColor: '#0f0f12',
  contentBgColor: '#18181b',
  fontPackId: 'quicksand',
  baseFontSize: 15,
  linkColor: '#60a5fa',
});

const CONTENT_WIDTH = 600;

// ── Helpers ────────────────────────────────────────────────────────────────

export function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function fontStack(fontPackId) {
  const pack = FONT_PACKS.find((p) => p.id === fontPackId);
  return (pack || FONT_PACKS[0]).stack;
}

function buttonRadius(styleVariant) {
  const s = BUTTON_STYLES.find((b) => b.id === styleVariant);
  return (s || BUTTON_STYLES[0]).borderRadius;
}

// Serialize a React-style camelCase style object to a CSS string. Numbers on
// known length props get "px"; unitless props (fontWeight, lineHeight) don't.
const UNITLESS = new Set(['fontWeight', 'lineHeight', 'opacity', 'zIndex']);
export function styleToCss(obj) {
  return Object.entries(obj || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => {
      const prop = k.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
      const val = typeof v === 'number' && !UNITLESS.has(k) ? `${v}px` : v;
      return `${prop}:${val}`;
    })
    .join(';');
}

let idCounter = 0;
function genId(prefix) {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter.toString(36)}`;
}

// Give Tiptap-authored <a> tags an inline link color (email clients won't
// inherit it). Only touches anchors that don't already carry a style attr.
function inlineLinkColor(html, linkColor) {
  return String(html || '').replace(/<a (?![^>]*\bstyle=)/gi, `<a style="color:${linkColor};text-decoration:underline" `);
}

// Tiptap emits bare <ul>/<ol>/<li>, and email clients zero out the default list
// margins/markers — so without inline styles, lists render with no bullets and
// no indent. Inject the styling inline (the email has no stylesheet). Skips tags
// that already carry a style attr and TaskList's checkbox lists (no marker by
// design); preserves any existing attributes via the capture group.
function inlineListStyles(html) {
  const UL = 'list-style-type:disc;margin:0.4em 0;padding-left:1.5em';
  const OL = 'list-style-type:decimal;margin:0.4em 0;padding-left:1.5em';
  const LI = 'margin:0.2em 0';
  return String(html || '')
    .replace(/<ul\b(?![^>]*\bstyle=)(?![^>]*\bdata-type=)([^>]*)>/gi, `<ul style="${UL}"$1>`)
    .replace(/<ol\b(?![^>]*\bstyle=)([^>]*)>/gi, `<ol style="${OL}"$1>`)
    .replace(/<li\b(?![^>]*\bstyle=)([^>]*)>/gi, `<li style="${LI}"$1>`);
}

// An empty paragraph (a bare Enter in the editor) is a visible blank line there
// — ProseMirror pads it with a trailing <br> — but getHTML() emits <p></p>,
// which collapses to nothing on the published page / in email. Pad it back so a
// blank line survives publish exactly as edited. Attributes (e.g. an inline
// text-align style) are preserved.
function preserveEmptyParagraphs(html) {
  return String(html || '').replace(/<p(\b[^>]*)?>\s*<\/p>/gi, '<p$1><br></p>');
}

// Blockquotes get a visible left bar inline — the UA default (a bare indent) is
// so subtle the Quote button looked like it did nothing. MUST stay in lockstep
// with the `.newsletter-rte blockquote` rule in tiptap.css so the editing
// surface matches the published page / email. Summaries get a pointer cursor so
// a collapsible section reads as clickable on the published page.
const BLOCKQUOTE_STYLE = 'border-left:3px solid rgba(128,128,128,0.5);margin:0.5em 0;padding:0.2em 0 0.2em 1em';
function inlineQuoteAndSummaryStyles(html) {
  return String(html || '')
    .replace(/<blockquote\b(?![^>]*\bstyle=)([^>]*)>/gi, `<blockquote style="${BLOCKQUOTE_STYLE}"$1>`)
    .replace(/<summary\b(?![^>]*\bstyle=)([^>]*)>/gi, '<summary style="cursor:pointer"$1>');
}

// Inline the site theme's H1/H2/H3 pixel sizes onto heading tags so the
// published page (which carries no stylesheet) renders them at the exact size
// the editor shows (the editor gets the same numbers via the --rte-h*-size CSS
// variables — see tiptap.css). Margins/weight stay at UA defaults on BOTH
// sides, so only font-size needs inlining. MERGES into an existing style attr
// (a TextAlign-ed heading carries style="text-align:…"), mirroring
// inlineImgStyles; a heading that already sets font-size is left alone.
function inlineHeadingStyles(html, sizes) {
  const withSize = (tag, px) => (m, attrs) => {
    const sm = attrs.match(/\bstyle="([^"]*)"/i);
    if (!sm) return `<${tag}${attrs} style="font-size:${px}px">`;
    if (/font-size\s*:/i.test(sm[1])) return m;
    const merged = `${sm[1].replace(/;\s*$/, '')};font-size:${px}px`;
    return `<${tag}${attrs.replace(/\bstyle="[^"]*"/i, `style="${merged}"`)}>`;
  };
  return String(html || '')
    .replace(/<h1\b([^>]*)>/gi, withSize('h1', sizes.h1))
    .replace(/<h2\b([^>]*)>/gi, withSize('h2', sizes.h2))
    .replace(/<h3\b([^>]*)>/gi, withSize('h3', sizes.h3));
}

// Allowlist matching EVERYTHING the shared TipTap extension set can produce
// (see src/features/inventory/components/RichTextViewer.tsx `tiptapExtensions`):
// marks, headings, lists, links, task lists, IMAGES, tables, and code blocks.
// Anything else — <script>, <iframe>, <img onerror>, inline event handlers,
// javascript: URLs — is stripped. This is the ONE place rich text is turned into
// raw HTML for dangerouslySetInnerHTML / email, so it must allow every node a
// text block legitimately contains (a missing tag here = content silently
// vanishing on the published site), while still blocking active content.
const RICH_TEXT_ALLOWED_TAGS = [
  'p', 'br', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'mark', 'sub', 'sup', 'small',
  'code', 'pre', 'blockquote',
  'details', 'summary', // collapsible section (native disclosure on the published page)
  'ul', 'ol', 'li', 'label', 'input', // input = TaskItem checkbox (anything but type=checkbox is removed)
  'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr',
  'img', 'figure', 'figcaption', 'video', 'source', // media: https / relative / data:image only (see richMediaUrlOk)
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'col', 'colgroup',
];
const RICH_TEXT_ALLOWED_ATTR = [
  'href', 'target', 'rel', 'style', 'class', 'start', 'dir',
  'data-type', 'data-checked', 'data-align', // TaskList/TaskItem + image alignment
  'src', 'alt', 'title', 'width', 'height', 'loading', 'referrerpolicy', // images (referrerpolicy is forced, see finishRichTextElement)
  'poster', 'controls', 'muted', 'loop', 'playsinline', 'preload', // <video> (no autoplay)
  'type', 'checked', 'disabled', // TaskItem checkbox <input>, <source type>
  'colspan', 'rowspan', 'colwidth', // tables
  'open', // <details> expanded state
];

// Sanitize untrusted rich-text HTML (authored content, pasted content, or a
// value crafted via the API) before it's inlined and rendered. DOMPurify also
// normalizes stray text — e.g. a legacy plain-text heading containing `<` or `&`
// comes back correctly escaped so it DISPLAYS as typed instead of breaking the
// page. ALLOW_DATA_ATTR is off (only the explicitly-listed data-type survives,
// for TaskList items).
//
// THE POLICY (documented in VIDOS docs/library/RICH_TEXT_EDIT.md, mirrored by VIDOS kit/RichHtml.cpp):
//   * Authors may load images and videos from other sites, so absolute `https:` is allowed for <img src>, <video src>,
//     <source src>, <video poster> and for CSS url() (background / background-image / list-style-image). Everything else is
//     not: javascript:, vbscript:, file:, plain http:, protocol-relative `//host`, blob:, and data: unless it is a base64
//     data:image/(png|jpeg|gif|webp|avif) (never svg) on an <img> (DOMPurify itself refuses data: on a poster). Relative URLs (the app's own /api/... media)
//     stay allowed in attributes. An external <img> gets referrerpolicy="no-referrer" (forced), so the third-party host
//     does not learn which page, or which private link, the reader is on.
//   * `style` is an ALLOW-LIST of properties with checked values (richCssDeclarationOk): typography, colours, box model,
//     borders, tables, lists, sizing, object-fit and a few harmless display keywords. Anything that can lay content over
//     the page (position, z-index, inset/top/left, transform, clip-path, filter) is not on it, so it is
//     dropped. Values may not contain CSS escapes (`\6a avascript`), expression(), @import, behavior/-moz-binding, unknown
//     functions or url() other than https, numbers over 10000 (or a margin below -100) or exponent notation.
//   * Elements outside the allow-list (script, style, iframe, object, embed, form, svg, math, meta, link, base, picture,
//     audio, track ...) go; scripts/styles/svg/math lose their content too. `srcset` and `ping` are not attributes we keep.
//   * <input> survives only as type=checkbox (the task-list box); a text field would be a fake login form.
//
// Rich text is authored by ANY account (self-serve homepages render it on the
// app's own origin), so presentation is constrained too: an inline `position`
// / `z-index` (or a `class` naming one of the app's utility classes, e.g.
// Tailwind's `fixed inset-0 z-50`) could lay a fake sign-in form over the real
// page. Inline styles lose those declarations; classes keep only the content
// classes the editor emits (callout, code-language tokens).
const RICH_TEXT_CLASS_RE = /^(callout[\w-]*|language-[\w-]+|hljs[\w-]*)$/;
// CSS comments (and an unterminated one, which runs to the end) are replaced by
// a space first (the way a browser's tokenizer separates tokens): `position/**/:fixed`
// and `/*;*/position:fixed` are both real declarations to a browser, and `1/**/0px` is two tokens.
const CSS_COMMENT_RE = /\/\*[\s\S]*?(\*\/|$)/g;

// ── URLs ──
const RICH_URL_MAX_BYTES = 4096;
// Never in a URL we accept: C0 controls, space, DEL, C1, NBSP and other Unicode spaces, zero-width and bidi marks, quotes, <>, backslash, backtick.
// (A browser strips tab/newline inside a URL and trims leading controls and spaces, which is how `java\nscript:` gets through
// a scheme check; refusing them outright is what keeps this check and the browser's parse the same.)
// eslint-disable-next-line no-control-regex -- the control characters are the point
const RICH_URL_BAD_CHAR_RE = /[\u0000-\u0020\u007f-\u00a0\u1680\u180e\u2000-\u200f\u2028-\u202f\u205f-\u206f\u3000\ufeff"'<>\\`]/;
const RICH_DATA_IMAGE_RE = /^data:image\/(?:png|jpeg|gif|webp|avif);base64,[A-Za-z0-9+/]*={0,2}$/i;

// An absolute https URL with a host and no userinfo (`https://trusted.com@evil.example/`).
function richHttpsUrlOk(url) {
  if (url.length > RICH_URL_MAX_BYTES || RICH_URL_BAD_CHAR_RE.test(url) || url.includes('(')) return false;
  const match = /^https:\/\/([^/?#]*)/i.exec(url);
  return match !== null && match[1] !== '' && !match[1].includes('@');
}

// A URL with no scheme that is not `//host`: it resolves on the page's own origin.
function richRelativeUrlOk(url) {
  if (url === '' || url.length > RICH_URL_MAX_BYTES || RICH_URL_BAD_CHAR_RE.test(url) || url.startsWith('//')) return false;
  const colon = url.indexOf(':');
  const delimiter = url.search(/[/?#]/);
  return colon === -1 || (delimiter !== -1 && delimiter < colon);
}

// The one rule for <img src>, <video src>, <source src> and <video poster> (data: only where the caller says so: <img>).
function richMediaUrlOk(url, allowDataImage) {
  if (allowDataImage && RICH_DATA_IMAGE_RE.test(url)) return true;
  return richHttpsUrlOk(url) || richRelativeUrlOk(url);
}

// ── CSS ──
const CSS_URL_PROPERTIES = new Set(['background', 'background-image', 'list-style', 'list-style-image']);
// Colours and gradients only. calc()/min()/max()/clamp() and bare parentheses are NOT here: jsdom's CSS parser (cssstyle) throws on malformed
// math (`calc(% - 20px)`, `min(1px,)`), and DOMPurify then returns an EMPTY string for the whole field, silently losing the document.
const CSS_FUNCTIONS = new Set(['rgb', 'rgba', 'hsl', 'hsla', 'hwb', 'linear-gradient', 'radial-gradient', 'conic-gradient',
  'repeating-linear-gradient', 'repeating-radial-gradient']);
const CSS_BAD_WORDS = new Set(['expression', 'behavior', 'behaviour', 'binding', 'javascript', 'vbscript', 'livescript', 'mocha']);
const CSS_KEYWORD_PROPERTIES = {
  display: new Set(['block', 'inline', 'inline-block', 'none', 'list-item', 'table', 'table-row', 'table-cell', 'table-row-group',
    'table-header-group', 'table-footer-group', 'table-column', 'table-column-group', 'table-caption', 'flex', 'inline-flex']),
  cursor: new Set(['auto', 'default', 'pointer', 'text', 'help', 'move', 'not-allowed', 'grab', 'grabbing', 'crosshair', 'wait', 'progress']),
};
const CSS_SIDES = ['top', 'right', 'bottom', 'left'];
const CSS_ALLOWED_PROPERTIES = new Set([
  'color', 'background', 'background-color', 'background-image', 'background-size', 'background-position', 'background-repeat',
  'background-clip', 'background-origin',
  'font', 'font-family', 'font-size', 'font-style', 'font-weight', 'font-variant', 'font-stretch', 'line-height', 'letter-spacing',
  'word-spacing', 'text-align', 'text-align-last', 'text-decoration', 'text-decoration-line', 'text-decoration-style',
  'text-decoration-color', 'text-decoration-thickness', 'text-indent', 'text-transform', 'text-shadow', 'text-overflow',
  'white-space', 'word-break', 'overflow-wrap', 'word-wrap', 'hyphens', 'vertical-align', 'direction', 'tab-size', 'orphans', 'widows',
  'margin', 'padding', 'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height', 'box-sizing', 'aspect-ratio',
  'object-fit', 'object-position', 'overflow', 'overflow-x', 'overflow-y', 'visibility', 'opacity', 'float', 'clear',
  'border', 'border-color', 'border-style', 'border-width', 'border-radius', 'border-collapse', 'border-spacing', 'box-shadow',
  'table-layout', 'caption-side', 'empty-cells',
  'list-style', 'list-style-type', 'list-style-position', 'list-style-image',
  'display', 'cursor',
  ...CSS_SIDES.flatMap((side) => [`margin-${side}`, `padding-${side}`, `border-${side}`, `border-${side}-color`, `border-${side}-style`,
    `border-${side}-width`]),
  'border-top-left-radius', 'border-top-right-radius', 'border-bottom-left-radius', 'border-bottom-right-radius',
]);
const CSS_MAX_NUMBER = 10000;
const CSS_MIN_MARGIN = -100;
const CSS_MAX_DECLARATION_BYTES = 4096;

const isCssIdentChar = (c) => /[A-Za-z0-9_-]/.test(c) || c.charCodeAt(0) >= 0x80;
const isCssDigit = (c) => c >= '0' && c <= '9';

// url( ... ) starting at value[open] ('(' of `url(`): the index after its `)`, or -1 when it is not an https URL.
function richCssUrlEnd(value, open) {
  let k = open + 1;
  while (k < value.length && ' \t\n\r\f'.includes(value[k])) k += 1;
  let url;
  if (value[k] === '"' || value[k] === "'") {
    const end = value.indexOf(value[k], k + 1);
    if (end === -1) return -1;
    url = value.slice(k + 1, end);
    k = end + 1;
    while (k < value.length && ' \t\n\r\f'.includes(value[k])) k += 1;
    if (value[k] !== ')') return -1;
  } else {
    const end = value.indexOf(')', k);
    if (end === -1) return -1;
    url = value.slice(k, end).trim();
    k = end;
  }
  return url !== '' && richHttpsUrlOk(url) ? k + 1 : -1;
}

// One CSS value, scanned token by token (linear, no regular expressions on the whole value).
function richCssValueOk(value, allowUrl, minNumber) {
  if (value === '' || value.includes('\\')) return false; // an escape spells any identifier (`\6a avascript`, `p\osition`)
  let depth = 0;
  let i = 0;
  while (i < value.length) {
    const c = value[i];
    const code = value.charCodeAt(i);
    if (c === '"' || c === "'") {
      const end = value.indexOf(c, i + 1);
      if (end === -1) return false;
      for (let k = i + 1; k < end; k += 1) {
        const inner = value.charCodeAt(k);
        if (inner < 0x20 || inner === 0x7f || value[k] === '<' || value[k] === '>') return false;
      }
      i = end + 1;
    } else if (/[A-Za-z_]/.test(c) || (c === '-' && i + 1 < value.length && /[A-Za-z_-]/.test(value[i + 1])) || code >= 0x80) {
      let j = i;
      while (j < value.length && isCssIdentChar(value[j])) j += 1;
      const name = value.slice(i, j).toLowerCase();
      if (value[j] === '(') {
        if (name === 'url') {
          if (!allowUrl) return false;
          const end = richCssUrlEnd(value, j);
          if (end === -1) return false;
          i = end;
          continue;
        }
        if (!CSS_FUNCTIONS.has(name)) return false;
        depth += 1;
        i = j + 1;
        continue;
      }
      if (CSS_BAD_WORDS.has(name)) return false;
      i = j;
    } else if (isCssDigit(c) || (c === '.' && isCssDigit(value[i + 1] || ''))) {
      let j = i;
      while (j < value.length && (isCssDigit(value[j]) || value[j] === '.')) j += 1;
      const number = Number(value.slice(i, j));
      const negative = i > 0 && value[i - 1] === '-';
      if (!Number.isFinite(number) || number > CSS_MAX_NUMBER || (negative && -number < minNumber)) return false;
      if ((value[j] === 'e' || value[j] === 'E') && (isCssDigit(value[j + 1] || '') || (/[+-]/.test(value[j + 1] || '') && isCssDigit(value[j + 2] || '')))) return false;
      i = j;
    } else if (c === '#') {
      // `0#fff` (a hash glued to a number or word) is an error to jsdom's CSS parser: see CSS_FUNCTIONS.
      if (i > 0 && /[A-Za-z0-9_.%-]/.test(value[i - 1])) return false;
      i += 1;
      while (i < value.length && /[0-9A-Za-z]/.test(value[i])) i += 1;
    } else if (c === ')') {
      depth -= 1;
      if (depth < 0) return false;
      i += 1;
    } else if (' \t\n\r\f,/%+-*.'.includes(c)) {
      i += 1;
    } else {
      return false; // @ { } [ ] < > ; : ! = $ & ? ^ ` ~ | and C0 controls
    }
  }
  return depth === 0;
}

// True when one `property: value` declaration is on the allow-list and its value is safe.
function richCssDeclarationOk(declaration) {
  if (declaration.length > CSS_MAX_DECLARATION_BYTES) return false;
  const colon = declaration.indexOf(':');
  if (colon < 1) return false;
  const property = declaration.slice(0, colon).trim().toLowerCase();
  if (!CSS_ALLOWED_PROPERTIES.has(property)) return false;
  const value = declaration.slice(colon + 1).replace(/\s*!\s*important\s*$/i, '').trim();
  const keywords = CSS_KEYWORD_PROPERTIES[property];
  if (keywords) return keywords.has(value.toLowerCase());
  return richCssValueOk(value, CSS_URL_PROPERTIES.has(property), property.startsWith('margin') ? CSS_MIN_MARGIN : -CSS_MAX_NUMBER);
}

// Declarations split on `;` that is not inside a string or parentheses (an https URL may contain `;`).
function splitCssDeclarations(css) {
  const out = [];
  let start = 0;
  let depth = 0;
  let quote = '';
  for (let i = 0; i < css.length; i += 1) {
    const c = css[i];
    if (quote) {
      if (c === quote) quote = '';
    } else if (c === '"' || c === "'") {
      quote = c;
    } else if (c === '(') {
      depth += 1;
    } else if (c === ')') {
      depth = Math.max(0, depth - 1);
    } else if (c === ';' && depth === 0) {
      out.push(css.slice(start, i));
      start = i + 1;
    }
  }
  out.push(css.slice(start));
  return out;
}

function constrainRichTextAttribute(node, data) {
  const tag = node.nodeName.toLowerCase();
  if (data.attrName === 'style') {
    // A declaration that is not on the allow-list is dropped; the rest is re-joined unchanged.
    data.attrValue = splitCssDeclarations(data.attrValue.replace(CSS_COMMENT_RE, ' ')).filter((declaration) => declaration.trim() && richCssDeclarationOk(declaration)).join(';');
  } else if (data.attrName === 'class') {
    data.attrValue = data.attrValue.split(/\s+/).filter((token) => RICH_TEXT_CLASS_RE.test(token)).join(' ');
  } else if (data.attrName === 'src') {
    if (!['img', 'video', 'source'].includes(tag) || !richMediaUrlOk(data.attrValue, tag === 'img')) data.keepAttr = false;
  } else if (data.attrName === 'poster') {
    if (tag !== 'video' || !richMediaUrlOk(data.attrValue, false)) data.keepAttr = false;
  } else if (data.attrName === 'referrerpolicy') {
    if (tag !== 'img') data.keepAttr = false;
  }
  if ((data.attrName === 'style' || data.attrName === 'class') && !data.attrValue) data.keepAttr = false;
}

// After an element's attributes are checked: an external image never sends the reader's page as its referrer, and the only
// <input> that stays is the task-list checkbox.
function finishRichTextElement(node) {
  const tag = node.nodeName.toLowerCase();
  if (tag === 'img') {
    if (/^https:/i.test(node.getAttribute('src') || '')) node.setAttribute('referrerpolicy', 'no-referrer');
    else node.removeAttribute('referrerpolicy');
  } else if (tag === 'input' && (node.getAttribute('type') || '').toLowerCase() !== 'checkbox') {
    node.remove();
  }
}

// Exported so the server can sanitize stored rich HTML on save with the SAME function the renderer uses
// (server/richHtmlSanitize.js) and the native editor can sanitize at its onChange boundary.
export function sanitizeRichHtml(html) {
  const input = String(html || '');
  if (!input) return '';
  // The hooks are added and removed around this one synchronous call, so other
  // DOMPurify users in the same bundle (SVG import, support mail) never see them.
  DOMPurify.addHook('uponSanitizeAttribute', constrainRichTextAttribute);
  DOMPurify.addHook('afterSanitizeAttributes', finishRichTextElement);
  const run = (source) => DOMPurify.sanitize(source, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
  try {
    const out = run(input);
    // jsdom's CSS parser throws on some malformed style values (`calc(% - 20px)`, `border:0#fff`) while it parses the document, and
    // DOMPurify swallows that and returns "" for the WHOLE field. A document that vanished is far worse than one that lost its
    // styles, so when nothing came back and the input had a style attribute, run it again without them.
    if (out === '' && /style\s*=/i.test(input)) return run(input.replace(/\sstyle\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, ''));
    return out;
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', finishRichTextElement);
    DOMPurify.removeHook('uponSanitizeAttribute', constrainRichTextAttribute);
  }
}

// Constrain images the way the editor's stylesheet does (`.tiptap-editor img` /
// `.newsletter-rte img`: max-width:100%) — the published page and email have no
// stylesheet, so without this an image renders at its intrinsic size and
// overflows. Alignment (display:block;margin:auto) is already baked into the
// img's inline style by the editor; we MERGE the size rules in (or set them when
// the img has no style) so an aligned image stays both centered AND constrained.
function inlineImgStyles(html) {
  const SIZE = 'max-width:100%;height:auto';
  return String(html || '').replace(/<(img|video)\b([^>]*?)\s*\/?>/gi, (_m, tag, attrs) => {
    const sm = attrs.match(/\bstyle="([^"]*)"/i);
    if (!sm) return `<${tag}${attrs} style="${SIZE}">`;
    if (/max-width\s*:/i.test(sm[1])) return `<${tag}${attrs}>`; // already constrained
    const merged = `${sm[1].replace(/;\s*$/, '')};${SIZE}`;
    return `<${tag}${attrs.replace(/\bstyle="[^"]*"/i, `style="${merged}"`)}>`;
  });
}

// Inline-style Tiptap-authored rich text so it renders the SAME everywhere the
// editor's `.newsletter-rte` stylesheet isn't loaded (email clients, and the
// public site page which is TipTap-free). Applies list markers/indent + link
// color + image sizing inline. Keep this the single source of truth so the
// editor, email, and site preview/production never drift. `linkColor` defaults
// to the muted footer link color used by custom footers.
//
// The raw HTML is SANITIZED first, then our own trusted inline styles are added
// (so they can't be stripped). Results are memoized: this runs on every React
// render of every text/hero block, and DOMPurify is the only non-trivial cost —
// identical (html, linkColor) inputs (the common case) return instantly.
const RICH_HTML_CACHE = new Map();
const RICH_HTML_CACHE_MAX = 500;
// `headingSizes` ({h1,h2,h3} in px) is the site builder's theme heading scale;
// omitted for email (headings there keep UA-default sizes, as before).
export function prepareRichTextHtml(html, linkColor = '#a1a1aa', headingSizes = null) {
  const raw = String(html || '');
  if (!raw) return '';
  const key = `${linkColor} ${headingSizes ? `${headingSizes.h1}/${headingSizes.h2}/${headingSizes.h3}` : ''} ${raw}`;
  const cached = RICH_HTML_CACHE.get(key);
  if (cached !== undefined) return cached;
  let out = inlineImgStyles(inlineQuoteAndSummaryStyles(inlineListStyles(inlineLinkColor(preserveEmptyParagraphs(sanitizeRichHtml(raw)), linkColor))));
  if (headingSizes) out = inlineHeadingStyles(out, headingSizes);
  // Simple bound: clear when full (LRU isn't worth it for tiny static strings).
  if (RICH_HTML_CACHE.size >= RICH_HTML_CACHE_MAX) RICH_HTML_CACHE.clear();
  RICH_HTML_CACHE.set(key, out);
  return out;
}

// The unsubscribe link is mandatory for CAN-SPAM and the List-Unsubscribe
// header, so the footer renderer auto-appends it. To avoid duplicating it (or
// silently losing it if the user deletes it), we strip any unsubscribe
// references from the editable footer html and let the renderer add the
// canonical link back. Run on both load and render.
export function stripUnsubscribeFromFooterHtml(html) {
  if (!html) return '';
  let out = String(html);
  // Drop whole <p>…</p> blocks containing the token.
  out = out.replace(/<p\b[^>]*>(?:(?!<\/p>)[\s\S])*\{\{unsubscribe_url\}\}(?:(?!<\/p>)[\s\S])*<\/p>/gi, '');
  // Fallback: a bare anchor that survived the paragraph match.
  out = out.replace(/<a\b[^>]*\{\{unsubscribe_url\}\}[\s\S]*?<\/a>/gi, '');
  // Last-resort literal token leftover.
  out = out.replace(/\{\{unsubscribe_url\}\}/g, '');
  return out.trim();
}

// Apply on-load fixups so editors and renderers see a consistent shape.
// Currently: strips the unsubscribe paragraph from older drafts whose footer
// still embedded the link inline (it is now auto-appended).
export function normalizeDoc(doc) {
  if (!doc || typeof doc !== 'object') return doc;
  const footer = doc.footer ? { ...doc.footer } : doc.footer;
  if (footer && typeof footer.html === 'string') {
    footer.html = stripUnsubscribeFromFooterHtml(footer.html);
  }
  const header = doc.header ? { bannerImage: false, ...doc.header } : doc.header;
  return { ...doc, header, footer };
}

// ── Factories ──────────────────────────────────────────────────────────────

export function createBlock(type) {
  const id = genId(type);
  switch (type) {
    case 'text':
      return {
        id, type: 'text',
        html: '<p>New text block — click to edit.</p>',
        fontSize: null, // null = inherit theme.baseFontSize
        textColor: '#d4d4d8',
        sectionColor: 'transparent',
        align: 'left',
        letterSpacing: 0,
        fontPackId: null, // null = inherit theme.fontPackId
        paddingY: 0,
        paddingX: 35,
      };
    case 'image':
      return {
        id, type: 'image',
        src: '', alt: '',
        width: 100, // percent of content width
        align: 'center',
        banner: false,
        // Default every new image to link to the site so a click goes to the
        // store page rather than opening the raw image full-size. Clearable /
        // editable per-image via the inspector's "Link URL" field; the renderer
        // wraps the <img> in an <a> whenever href is set.
        href: 'https://sleepycircuits.com',
        sectionColor: 'transparent',
        spacing: 0,
        showCaption: false,
        caption: '',
      };
    case 'button':
      return {
        id, type: 'button',
        label: 'Click here',
        href: 'https://',
        styleVariant: 'rounded',
        bgColor: '#3b82f6',
        textColor: '#ffffff',
        sectionColor: 'transparent',
        align: 'center',
        fontSize: 15,
        fontPackId: null,
        letterSpacing: 0,
        bold: true, italic: false, underline: false,
        paddingX: 22, paddingY: 0,        // button pill interior
        blockPaddingX: 32, blockPaddingY: 0, // surrounding block cell
      };
    case 'line':
      return {
        id, type: 'line',
        color: '#3f3f46',
        thickness: 1,
        width: 100, // percent
        sectionColor: 'transparent',
        paddingY: 0,
      };
    case 'spacer':
      return {
        id, type: 'spacer',
        height: 24,
        sectionColor: 'transparent',
      };
    default:
      throw new Error(`Unknown block type: ${type}`);
  }
}

export function createDefaultDoc() {
  return {
    subject: 'Sleepy Circuits Newsletter',
    preheaderText: '',
    theme: { ...DEFAULT_THEME },
    header: {
      id: 'header', type: 'header',
      logoSrc: '',
      title: 'Sleepy Circuits',
      titleColor: '#fafafa',
      titleFontSize: 22,
      sectionColor: 'transparent',
      align: 'center',
      paddingY: 0,
      paddingX: 35,
      fontPackId: null, // null = inherit theme.fontPackId
      bannerImage: false,
    },
    blocks: [createBlock('text')],
    footer: {
      id: 'footer', type: 'footer',
      // The unsubscribe link is auto-appended by the renderer — don't put
      // it here, or it would either duplicate or be deletable by the user.
      html: "<p>You're receiving this because you opted into marketing emails from Sleepy Circuits.</p>",
      textColor: '#71717a',
      sectionColor: 'transparent',
      fontSize: 11,
      paddingY: 0,
      paddingX: 0,
      fontPackId: null, // null = inherit theme.fontPackId
    },
  };
}

// ── Per-block style objects (shared by canvas + renderer) ──────────────────

export function sectionStyle(block) {
  return {
    backgroundColor: block.sectionColor && block.sectionColor !== 'transparent' ? block.sectionColor : null,
  };
}

// Effective background colour behind a block's content: its own sectionColor
// when set, otherwise the email content background. The inline rich-text editor
// uses this so the editing surface matches the block instead of showing a white
// box (which hides light text meant for a dark background).
export function surfaceBackground(block, theme) {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  if (block && block.sectionColor && block.sectionColor !== 'transparent') {
    return block.sectionColor;
  }
  return bgColor(t);
}

export function headerStyle(block) {
  // In banner-image mode the image must touch the edges of the email card,
  // so the cell drops its own padding. A title (if present) re-introduces
  // padding around itself via headerTitleBoxStyle.
  const bannerMode = block.bannerImage && block.logoSrc;
  return {
    padding: bannerMode ? '0' : `${block.paddingY}px ${block.paddingX ?? 35}px`,
    textAlign: block.align,
    backgroundColor: block.sectionColor !== 'transparent' ? block.sectionColor : null,
  };
}

export function headerImageStyle(block) {
  if (block.bannerImage) {
    // Full-bleed banner: fills the email width edge-to-edge.
    return {
      width: '100%',
      maxWidth: '100%',
      height: 'auto',
      display: 'block',
      border: '0',
    };
  }
  return {
    maxWidth: 200,
    height: 'auto',
    display: 'inline-block',
    border: '0',
  };
}

export function headerTitleBoxStyle(block) {
  // Wraps the title when it sits next to a banner image — gives it back the
  // padding that headerStyle had to drop.
  return {
    padding: `${block.paddingY}px ${block.paddingX ?? 35}px`,
    textAlign: block.align,
  };
}

export function headerTitleStyle(block, theme) {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  return {
    margin: '0',
    fontSize: block.titleFontSize,
    fontWeight: 700,
    color: block.titleColor,
    fontFamily: fontStack(block.fontPackId || t.fontPackId),
  };
}

export function textBlockStyle(block) {
  return {
    padding: `${block.paddingY}px ${block.paddingX ?? 0}px`,
    textAlign: block.align,
    backgroundColor: block.sectionColor !== 'transparent' ? block.sectionColor : null,
  };
}

export function textContentStyle(block, theme) {
  return {
    fontSize: block.fontSize || theme.baseFontSize,
    color: block.textColor,
    fontFamily: fontStack(block.fontPackId || theme.fontPackId),
    letterSpacing: block.letterSpacing ? `${block.letterSpacing}px` : null,
    lineHeight: 1.5,
  };
}

export function buttonBlockStyle(block) {
  // Wrapper ("main") padding around the button. Nullish-fallback so older
  // button blocks without these fields still render (0 vertical / 32 horizontal).
  const py = block.blockPaddingY ?? 0;
  const px = block.blockPaddingX ?? 32;
  return {
    padding: `${py}px ${px}px`,
    textAlign: block.align,
    backgroundColor: block.sectionColor !== 'transparent' ? block.sectionColor : null,
  };
}

export function buttonStyle(block, theme) {
  return {
    display: 'inline-block',
    backgroundColor: block.bgColor,
    color: block.textColor,
    fontFamily: fontStack(block.fontPackId || theme.fontPackId),
    fontSize: block.fontSize,
    fontWeight: block.bold ? 700 : 400,
    fontStyle: block.italic ? 'italic' : 'normal',
    textDecoration: block.underline ? 'underline' : 'none',
    letterSpacing: block.letterSpacing ? `${block.letterSpacing}px` : null,
    padding: `${block.paddingY}px ${block.paddingX}px`,
    borderRadius: buttonRadius(block.styleVariant),
  };
}

export function imageBlockStyle(block) {
  return {
    padding: block.banner ? '0' : `${block.spacing}px 32px`,
    textAlign: block.align,
    backgroundColor: block.sectionColor !== 'transparent' ? block.sectionColor : null,
  };
}

export function imageStyle(block) {
  return {
    width: block.banner ? '100%' : `${block.width}%`,
    maxWidth: '100%',
    height: 'auto',
    display: 'inline-block',
    border: '0',
  };
}

export function captionStyle() {
  return { margin: '6px 0 0', fontSize: 11, color: '#71717a' };
}

export function lineBlockStyle(block) {
  return {
    padding: `${block.paddingY}px 32px`,
    backgroundColor: block.sectionColor !== 'transparent' ? block.sectionColor : null,
  };
}

export function lineStyle(block) {
  return {
    border: '0',
    borderTop: `${block.thickness}px solid ${block.color}`,
    width: `${block.width}%`,
    margin: '0 auto',
  };
}

export function spacerStyle(block) {
  return {
    height: block.height,
    lineHeight: `${block.height}px`,
    fontSize: '1px',
    backgroundColor: block.sectionColor !== 'transparent' ? block.sectionColor : null,
  };
}

export function footerStyle(block) {
  return {
    padding: `${block.paddingY}px ${block.paddingX ?? 0}px`,
    backgroundColor: block.sectionColor !== 'transparent' ? block.sectionColor : null,
  };
}

export function footerContentStyle(block, theme) {
  return {
    fontSize: block.fontSize,
    color: block.textColor,
    fontFamily: fontStack(block.fontPackId || theme.fontPackId),
    lineHeight: 1.5,
    textAlign: 'center',
  };
}

// ── HTML renderer (email-safe) ─────────────────────────────────────────────

// Header / body / footer blocks paired with a stable per-block class. Shared by
// the HTML renderer (applies the classes) and the dark-mode override builder
// (targets them) so the two can never drift out of lockstep.
function enumerateBlocks(doc) {
  return [
    { block: doc.header, cls: 'nl-h' },
    ...(doc.blocks || []).map((b, i) => ({ block: b, cls: `nl-b${i}` })),
    { block: doc.footer, cls: 'nl-f' },
  ].filter((e) => e.block);
}

// The single full-bleed background color. The email is one uniform color edge-to-
// edge (canvas + content table + every cell all paint this), with content kept in
// a centered 600px column — see renderBlocksToHtml. emailBgColor is the canonical
// field; contentBgColor is deprecated (no longer read) so old saved docs that
// still carry a distinct value render uniformly without migration.
function bgColor(theme) {
  return theme.emailBgColor;
}

// The effective background a block's cell paints — its own section color, or the
// single email background when the section is transparent. Never null, so no cell
// falls through to a client-painted canvas in dark mode (and so Gmail, which cuts
// off any color not on inner cells, keeps the background everywhere).
function cellBgFor(block, theme) {
  return block.sectionColor && block.sectionColor !== 'transparent'
    ? block.sectionColor
    : bgColor(theme);
}

function td(styleObj, inner, cls = '', bgFallback = null) {
  // Explicit bgcolor + class on every cell. Dark-mode clients that ignore
  // color-scheme (notably the Gmail mobile app) recolor cells and tag them with
  // data-ogsb; the class lets buildDarkOverrideCss() re-assert the authored
  // background. bgFallback fills transparent blocks with the card background.
  const bg = styleObj.backgroundColor || bgFallback || null;
  const finalStyle = bg ? { ...styleObj, backgroundColor: bg } : styleObj;
  const clsAttr = cls ? ` class="${cls}"` : '';
  const bgAttr = bg ? ` bgcolor="${bg}"` : '';
  return `<tr><td${clsAttr}${bgAttr} style="${styleToCss(finalStyle)}">${inner}</td></tr>`;
}

// Per-element overrides that re-apply the authored colors when a dark-mode
// client (Gmail/Outlook app) rewrites them. The data-ogsc (text) / data-ogsb
// (background) attributes are added by the client ONLY in dark mode, so every
// rule here is an inert no-op in light mode and in clients that honor
// color-scheme. Selectors cover both "ancestor tagged" and "element tagged".
function buildDarkOverrideCss(doc, theme) {
  const rules = [
    `[data-ogsb] .nl-canvas,.nl-canvas[data-ogsb]{background-color:${bgColor(theme)}!important}`,
    `[data-ogsb] .nl-content,.nl-content[data-ogsb]{background-color:${bgColor(theme)}!important}`,
  ];
  for (const { block, cls } of enumerateBlocks(doc)) {
    rules.push(`[data-ogsb] .${cls},.${cls}[data-ogsb]{background-color:${cellBgFor(block, theme)}!important}`);
    const textColor =
      block.type === 'header' ? block.titleColor
        : (block.type === 'text' || block.type === 'footer') ? block.textColor
          : null;
    if (textColor) {
      rules.push(`[data-ogsc] .${cls}-t,.${cls}-t[data-ogsc]{color:${textColor}!important}`);
    }
    if (block.type === 'button') {
      rules.push(`[data-ogsb] .${cls}-btn,.${cls}-btn[data-ogsb]{background-color:${block.bgColor}!important}`);
      rules.push(`[data-ogsc] .${cls}-btn,.${cls}-btn[data-ogsc]{color:${block.textColor}!important}`);
    }
  }
  return rules.join('\n');
}

// `opts.unsubscribe` (default true) controls the mandatory marketing
// unsubscribe row in the footer. Transactional/system emails pass
// `{ unsubscribe: false }` — CAN-SPAM's unsubscribe requirement applies to
// marketing mail, and putting an "Unsubscribe" link on a password-reset or
// verification email is both wrong and confusing.
export function renderBlockHtml(block, theme, opts = {}, cls = '') {
  // bgFallback keeps transparent cells painted with the single email background
  // so dark-mode clients can't show a light canvas behind them; `cls`/`cls-t`/
  // `cls-btn` give buildDarkOverrideCss() hooks to re-assert colors. cls is '' for
  // the live preview (no dark-mode rewriting happens in a browser), which is fine.
  const bgFallback = bgColor(theme);
  const textCls = cls ? ` class="${cls}-t"` : '';
  switch (block.type) {
    case 'header': {
      const bannerMode = block.bannerImage && block.logoSrc;
      const logo = block.logoSrc
        ? `<img src="${escapeHtml(block.logoSrc)}" alt="${escapeHtml(block.title)}" style="${styleToCss(headerImageStyle(block))}" />`
        : '';
      const titleEl = block.title
        ? `<div${textCls} style="${styleToCss(headerTitleStyle(block, theme))}">${escapeHtml(block.title)}</div>`
        : '';
      if (bannerMode) {
        // Banner image fills the cell; if the user kept a title, it sits
        // in its own padded row below the image.
        const titleRow = titleEl ? `<div style="${styleToCss(headerTitleBoxStyle(block))}">${titleEl}</div>` : '';
        return td(headerStyle(block), `${logo}${titleRow}`, cls, bgFallback);
      }
      const inner = logo && titleEl ? `${logo}<div style="height:12px"></div>${titleEl}` : logo || titleEl;
      return td(headerStyle(block), inner, cls, bgFallback);
    }
    case 'text': {
      const content = prepareRichTextHtml(block.html, theme.linkColor);
      const inner = `<div${textCls} style="${styleToCss(textContentStyle(block, theme))}">${content}</div>`;
      return td(textBlockStyle(block), inner, cls, bgFallback);
    }
    case 'button': {
      const btnCls = cls ? ` class="${cls}-btn"` : '';
      const btn = `<a href="${escapeHtml(block.href)}" target="_blank" rel="noopener"${btnCls} style="${styleToCss(buttonStyle(block, theme))}">${escapeHtml(block.label)}</a>`;
      return td(buttonBlockStyle(block), btn, cls, bgFallback);
    }
    case 'image': {
      if (!block.src) {
        return td(imageBlockStyle(block), '<div style="padding:24px;color:#71717a;font-size:12px;text-align:center;border:1px dashed #3f3f46">No image selected</div>', cls, bgFallback);
      }
      let img = `<img src="${escapeHtml(block.src)}" alt="${escapeHtml(block.alt)}" style="${styleToCss(imageStyle(block))}" />`;
      if (block.href) {
        img = `<a href="${escapeHtml(block.href)}" target="_blank" rel="noopener">${img}</a>`;
      }
      const caption = block.showCaption && block.caption
        ? `<div style="${styleToCss(captionStyle())}">${escapeHtml(block.caption)}</div>`
        : '';
      return td(imageBlockStyle(block), img + caption, cls, bgFallback);
    }
    case 'line':
      return td(lineBlockStyle(block), `<hr style="${styleToCss(lineStyle(block))}" />`, cls, bgFallback);
    case 'spacer':
      return td(spacerStyle(block), '&nbsp;', cls, bgFallback);
    case 'footer': {
      const customHtml = stripUnsubscribeFromFooterHtml(block.html);
      const customContent = prepareRichTextHtml(customHtml, '#a1a1aa');
      // Always-on unsubscribe row for marketing mail. CAN-SPAM and the
      // List-Unsubscribe header both require this link to exist, so it's not
      // user-editable — but transactional emails opt out via opts.unsubscribe.
      const unsubscribeHtml = opts.unsubscribe === false
        ? ''
        : '<p style="margin:8px 0 0"><a href="{{unsubscribe_url}}" style="color:#a1a1aa;text-decoration:underline">Unsubscribe</a></p>';
      const inner = `<div${textCls} style="${styleToCss(footerContentStyle(block, theme))}">${customContent}${unsubscribeHtml}</div>`;
      return td(footerStyle(block), inner, cls, bgFallback);
    }
    default:
      return '';
  }
}

export function renderBlocksToHtml(doc, opts = {}) {
  const theme = { ...DEFAULT_THEME, ...(doc.theme || {}) };
  const rows = enumerateBlocks(doc)
    .map(({ block, cls }) => renderBlockHtml(block, theme, opts, cls))
    .join('\n');
  // No border-radius: rounded corners clip a banner header/image and reveal a
  // sliver of the email background above it (the "hairline"). Square corners let
  // a full-bleed image sit flush to the very top edge of the content column.
  // The centered content column paints the SAME single background as the
  // full-width canvas, so the email reads as one uniform full-bleed color (no
  // two-tone card) while content stays capped at 600px and centered via the
  // outer <td align="center"> in wrapNewsletterHtml.
  const bg = bgColor(theme);
  return `<table role="presentation" class="nl-content" width="${CONTENT_WIDTH}" cellpadding="0" cellspacing="0" border="0" bgcolor="${bg}" style="width:100%;max-width:${CONTENT_WIDTH}px;background:${bg}">
<tbody>
${rows}
</tbody>
</table>`;
}

// Full email document: doctype + body bg + centered content + hidden preheader.
// `opts.unsubscribe: false` suppresses the marketing unsubscribe footer for
// transactional/system emails.
// Perceived luminance (0 = black, 1 = white) of a #rgb / #rrggbb color, used to
// decide which color scheme an email was authored in. Falls back to "light" on
// an unparseable value so we never accidentally force a dark canvas under
// dark-text content.
function colorLuminance(hex) {
  const h = String(hex || '').replace(/^#/, '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (!/^[0-9a-f]{6}$/i.test(full)) return 1;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// The single scheme an email should declare itself as. Declaring one fixed
// scheme (instead of "light dark") is what tells Apple Mail / iOS Mail / Outlook
// "this email is already authored for this theme — don't recolor it", so every
// recipient sees the same thing regardless of their device dark/light setting.
function colorSchemeFor(bgColor) {
  return colorLuminance(bgColor) < 0.5 ? 'dark' : 'light';
}

export function wrapNewsletterHtml(doc, opts = {}) {
  const theme = { ...DEFAULT_THEME, ...(doc.theme || {}) };
  const scheme = colorSchemeFor(theme.emailBgColor);
  const preheader = doc.preheaderText
    ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent">${escapeHtml(doc.preheaderText)}</div>`
    : '';
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<!-- Lock the email to the scheme it was DESIGNED in (computed from its own
     background) so dark/light-mode clients don't invert or recolor it. This is
     what keeps the email looking identical on phone vs desktop and in light vs
     dark mode — the recipient's theme preference is intentionally ignored. -->
<meta name="color-scheme" content="${scheme}" />
<meta name="supported-color-schemes" content="${scheme}" />
<title>${escapeHtml(doc.subject)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${GOOGLE_FONTS_LINK}" />
<!-- @import as well as <link>: some web-font-capable clients (Apple Mail, iOS
     Mail) honor one or the other. Gmail/Outlook ignore BOTH and fall back to
     the sans-serif in each element's inline font-family stack — that's expected;
     custom web fonts simply aren't supported there. -->
<style type="text/css">
@import url('${GOOGLE_FONTS_LINK}');
:root { color-scheme: ${scheme}; supported-color-schemes: ${scheme}; }
/* Re-assert the authored background for clients that honor prefers-color-scheme
   and would otherwise paint their own dark/light canvas behind the content. */
@media (prefers-color-scheme: dark) {
  body, .nl-canvas { background:${theme.emailBgColor} !important; }
}
@media (prefers-color-scheme: light) {
  body, .nl-canvas { background:${theme.emailBgColor} !important; }
}
/* Gmail / Outlook app dark mode: these clients ignore color-scheme and recolor
   the email, tagging changed elements with data-ogsc/data-ogsb. Re-assert each
   authored color so the design survives. Inert everywhere else. */
${buildDarkOverrideCss(doc, theme)}
</style>
</head>
<body style="margin:0;padding:0;background:${theme.emailBgColor};font-family:${fontStack(theme.fontPackId)}">
${preheader}
<table role="presentation" class="nl-canvas" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${theme.emailBgColor}" style="background:${theme.emailBgColor}">
<tbody><tr><td align="center" class="nl-canvas" bgcolor="${theme.emailBgColor}" style="padding:0;background:${theme.emailBgColor}">
${renderBlocksToHtml(doc, opts)}
</td></tr></tbody>
</table>
</body>
</html>`;
}

// The single representative image for a campaign — used as the lightweight
// "poster" on a profile's Email Highlights circle instead of rendering the whole
// email. Prefers the header logo/banner, then the first image block. Returns null
// when the email has no usable image (the caller shows a themed fallback).
export function posterImageFor(doc) {
  if (!doc || typeof doc !== 'object') return null;
  const header = doc.header;
  if (header && typeof header.logoSrc === 'string' && header.logoSrc.trim()) return header.logoSrc;
  const blocks = Array.isArray(doc.blocks) ? doc.blocks : [];
  const isImg = (b) => b && b.type === 'image' && typeof b.src === 'string' && b.src.trim();
  const banner = blocks.find((b) => isImg(b) && b.banner);
  if (banner) return banner.src;
  const any = blocks.find(isImg);
  return any ? any.src : null;
}

// ── Plain-text renderer ────────────────────────────────────────────────────

function htmlToPlain(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<li>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function renderBlockText(block, opts = {}) {
  switch (block.type) {
    case 'header':
      return [block.title, block.logoSrc ? `[logo: ${block.logoSrc}]` : ''].filter(Boolean).join('\n');
    case 'text':
      return htmlToPlain(block.html);
    case 'button':
      return `${block.label}: ${block.href}`;
    case 'image': {
      const parts = [];
      if (block.src) parts.push(`[image: ${block.alt || block.src}]`);
      if (block.showCaption && block.caption) parts.push(block.caption);
      return parts.join('\n');
    }
    case 'line':
      return '----------------------------------------';
    case 'spacer':
      return '';
    case 'footer': {
      const custom = htmlToPlain(stripUnsubscribeFromFooterHtml(block.html));
      const unsub = opts.unsubscribe === false ? '' : 'Unsubscribe: {{unsubscribe_url}}';
      return [custom, unsub].filter(Boolean).join('\n\n');
    }
    default:
      return '';
  }
}

export function renderBlocksToText(doc, opts = {}) {
  return [
    renderBlockText(doc.header, opts),
    ...(doc.blocks || []).map((b) => renderBlockText(b, opts)),
    renderBlockText(doc.footer, opts),
  ].filter((s) => s !== '').join('\n\n');
}

export function wrapNewsletterText(doc, opts = {}) {
  return renderBlocksToText(doc, opts);
}

// ── Validation ─────────────────────────────────────────────────────────────

// The unsubscribe link is auto-appended by the renderer (see the footer case
// in renderBlockHtml), so we no longer require it in the editable html.
export function validateDoc(doc) {
  const errors = [];
  if (!doc || typeof doc !== 'object') return ['Document is missing.'];
  if (!doc.subject || !String(doc.subject).trim()) errors.push('Subject line is required.');
  if (!Array.isArray(doc.blocks)) errors.push('Document has no content blocks.');
  return errors;
}

// ── Email image optimization (send-time swap to the progressive sm JPEG) ────
//
// Sent emails can't run the site's JS tier ladder, so at send time image-block
// srcs are swapped to the media's `image_sm_jpeg` variant — a 960px
// PROGRESSIVE JPEG (renders blur→sharp natively in mail clients) that also
// serves as the site's small-tier JPEG fallback. These helpers are isomorphic
// on purpose: the client-side variant generator and the backend sender share
// one source-eligibility policy and one URL-rewrite convention, so they can't
// drift.

// Source mimes whose pixels are guaranteed opaque AND still (no animation) —
// the only ones an email-embedded JPEG can faithfully represent. Deliberately
// excludes png/gif/svg/webp/avif: transparency or animation can't be ruled out
// from the mime alone, and a white-flattened or frozen swap would visibly
// corrupt the email. Media outside this list simply keeps its original URL.
export const EMAIL_JPEG_SOURCE_MIME_RE = /^image\/(jpeg|pjpeg|heic|heif|tiff|bmp)$/i;

export function isEmailJpegSource(mime) {
  return EMAIL_JPEG_SOURCE_MIME_RE.test(mime || '');
}

// Matches an untouched ABSOLUTE original media URL (`<origin>/api/media-uploads/<uuid>`,
// nothing after) — the exact shape the library pick/upload resolvers write into
// blocks. Relative srcs are skipped (already broken in email today — no origin
// to resolve against in an inbox), as is anything with a path/query suffix.
const MEDIA_UPLOAD_SRC_RE = /^(https?:\/\/[^/]+)\/api\/media-uploads\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * Media ids referenced by IMAGE blocks' `src`. Header logos are deliberately
 * excluded (frequently transparent PNGs) and rich-text-embedded <img>s are out
 * of scope — only structured image blocks participate in the swap.
 */
export function collectNewsletterImageMediaIds(doc) {
  const ids = [];
  for (const b of (doc && Array.isArray(doc.blocks) ? doc.blocks : [])) {
    if (b && b.type === 'image' && typeof b.src === 'string') {
      const m = MEDIA_UPLOAD_SRC_RE.exec(b.src);
      if (m && !ids.includes(m[2])) ids.push(m[2]);
    }
  }
  return ids;
}

/**
 * Deep-clones `doc` and rewrites each image block whose src matches an
 * original media URL with an id in `readyIds` (a Set) to that media's
 * permanent public progressive-JPEG URL:
 *   `<same origin>/api/media-uploads/<id>/variants/image_sm_jpeg/raw`
 * NEVER a signed CDN URL — those expire, and inbox HTML is immutable. The
 * `/raw` route is public for published media and immutable-cached.
 */
export function swapNewsletterImagesToEmailVariant(doc, readyIds) {
  const out = JSON.parse(JSON.stringify(doc));
  for (const b of (Array.isArray(out.blocks) ? out.blocks : [])) {
    if (b && b.type === 'image' && typeof b.src === 'string') {
      const m = MEDIA_UPLOAD_SRC_RE.exec(b.src);
      if (m && readyIds.has(m[2])) {
        b.src = `${m[1]}/api/media-uploads/${m[2]}/variants/image_sm_jpeg/raw`;
      }
    }
  }
  return out;
}

// ── System (transactional) emails ───────────────────────────────────────────
//
// The verification / password-reset / recovery-key emails reuse the same block
// model and renderer as newsletters, so admins can format them and add blocks.
// They differ in two ways: they carry no marketing unsubscribe footer (render
// with { unsubscribe: false }), and they expose per-template tokens that the
// backend substitutes at send time instead of the per-recipient newsletter
// tokens. The hardcoded builders in emailService.js remain the safety-net
// default whenever no custom template has been saved (or one fails to render).

function systemTextBlock(html, { muted = false } = {}) {
  const b = createBlock('text');
  b.html = html;
  b.align = 'left';
  if (muted) {
    b.fontSize = 12;
    b.textColor = '#71717a';
  }
  return b;
}

function systemButtonBlock(label, href) {
  const b = createBlock('button');
  b.label = label;
  b.href = href;
  b.align = 'left';
  return b;
}

function systemHeader(title) {
  return {
    id: 'header', type: 'header',
    logoSrc: '',
    title,
    titleColor: '#fafafa',
    titleFontSize: 20,
    sectionColor: 'transparent',
    align: 'left',
    paddingY: 24,
    bannerImage: false,
  };
}

function systemFooter() {
  return {
    id: 'footer', type: 'footer',
    // No unsubscribe — transactional emails render with { unsubscribe: false }.
    html: '',
    textColor: '#71717a',
    sectionColor: 'transparent',
    fontSize: 11,
    paddingY: 20,
  };
}

// One entry per editable system email. `tokens` drives the editor's insert
// menu; the backend maps each token to a live value at send time.
const SYSTEM_EMAIL_SPECS = [
  {
    id: 'verification',
    label: 'Account Verification',
    subject: 'Confirm your Sleepy Circuits account',
    tokens: [
      { token: '{{username}}', label: 'Username' },
      { token: '{{verify_url}}', label: 'Verification link' },
    ],
    intro: 'Welcome, {{username}}. Click the button below to activate your account. This link expires in 24 hours.',
    cta: 'Confirm email',
    url: '{{verify_url}}',
    footnote: "If you didn't request this, you can safely ignore this email — no account will be created.",
  },
  {
    id: 'password-reset',
    label: 'Password Reset',
    subject: 'Reset your Sleepy Circuits password',
    tokens: [
      { token: '{{matrix_user_id}}', label: 'User ID' },
      { token: '{{reset_url}}', label: 'Reset link' },
    ],
    intro: 'A password reset was requested for {{matrix_user_id}}. Click below to set a new password. This link expires in 30 minutes.',
    cta: 'Reset password',
    url: '{{reset_url}}',
    footnote: "If you didn't request this, you can ignore this email and your password will stay the same.",
  },
  {
    id: 'recovery-key',
    label: 'Welcome / Recovery Key',
    subject: 'Welcome to Sleepy Circuits — save your recovery key',
    tokens: [
      { token: '{{username}}', label: 'Username' },
      { token: '{{recovery_url}}', label: 'Recovery link' },
    ],
    intro: 'Welcome, {{username}}. This email contains your personal recovery key — the ONLY way to restore your encrypted messages if you lose this device or sign in somewhere new. We cannot recover it for you, so please keep this email somewhere safe.',
    cta: 'Restore my messages',
    url: '{{recovery_url}}',
    footnote: 'Do not share this link with anyone. Anyone who has it, together with access to your account, can read your entire message history.',
  },
  {
    id: 'newsletter-confirm',
    label: 'Newsletter Confirmation',
    subject: 'Confirm your subscription to {{list_name}}',
    tokens: [
      { token: '{{confirm_url}}', label: 'Confirmation link' },
      { token: '{{list_name}}', label: 'List / sender name' },
    ],
    intro: 'Thanks for signing up to {{list_name}}. Click the button below to confirm your email and start receiving updates. This link expires in 7 days.',
    cta: 'Confirm subscription',
    url: '{{confirm_url}}',
    footnote: "If you didn't request this, you can safely ignore this email — you won't be added to any list.",
  },
];

// Editor-facing catalog (id, label, tokens) — no factory functions leak out.
export const SYSTEM_EMAIL_TEMPLATES = Object.freeze(
  SYSTEM_EMAIL_SPECS.map((s) =>
    Object.freeze({ id: s.id, label: s.label, tokens: Object.freeze(s.tokens.map((t) => Object.freeze({ ...t }))) })),
);

export function isSystemEmailTemplate(id) {
  return SYSTEM_EMAIL_SPECS.some((s) => s.id === id);
}

export function systemEmailTokens(id) {
  const spec = SYSTEM_EMAIL_SPECS.find((s) => s.id === id);
  return spec ? spec.tokens : [];
}

// The canonical block doc for an as-yet-uncustomized system email. This is the
// single source of truth: the editor shows it AND emailService renders it for
// the actual send (via renderSystemEmailDoc → wrapNewsletterHtml), so the sent
// email always matches the editor and is full-bleed.
export function createSystemEmailDoc(templateId) {
  const spec = SYSTEM_EMAIL_SPECS.find((s) => s.id === templateId);
  if (!spec) throw new Error(`Unknown system email template: ${templateId}`);
  return {
    subject: spec.subject,
    preheaderText: '',
    theme: { ...DEFAULT_THEME },
    header: systemHeader(spec.subject),
    blocks: [
      systemTextBlock(`<p>${escapeHtml(spec.intro)}</p>`),
      systemButtonBlock(spec.cta, spec.url),
      systemTextBlock(`<p style="word-break:break-all">${escapeHtml(spec.url)}</p>`, { muted: true }),
      systemTextBlock(`<p>${escapeHtml(spec.footnote)}</p>`, { muted: true }),
    ],
    footer: systemFooter(),
  };
}
